import {
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Output, EventEmitter } from '@angular/core';
@Component({
	selector: 'cmv-range-input',
	templateUrl: './range-input.component.html',
	styleUrls: ['./range-input.component.scss'],
})
export class RangeInputComponent implements OnInit, OnChanges {
	@Input() mininumAmount: number = 5000;
	@Input() maximumAmount: number = 15000;
	@Input() amount: number = 5000;
	@Input() jump: string = '1';
	@Input() isDecrementButtonDisable: boolean = false;
	@Input() isIncrementButtonDisable: boolean = false;
	@Input() isManualChangeDisable: boolean = false;
	@Input() isDark: boolean = false;
	@Input() enableQuickSelection: boolean = false;
	@Input() quickAmounts: number[] = [10000, 25000, 50000];

	@Output() rangeAmountChange = new EventEmitter<number>();
	@Output() amountLiveChange = new EventEmitter<number>();
	@ViewChild('amountInput') amountInput?: ElementRef<HTMLInputElement>;
	formattedAmount: string;
	sliderProgress: number = 0;
	isEditing: boolean = false;

	constructor(private currencyPipe: CurrencyPipe) {
		this.formattedAmount = this.getFormattedAmount();
	}

	get visibleQuickAmounts(): number[] {
		return this.quickAmounts
			.filter(
				amount => amount > this.mininumAmount && amount < this.maximumAmount
			)
			.sort((a, b) => a - b);
	}

	startEditing(): void {
		if (this.isManualChangeDisable) {
			return;
		}
		this.isEditing = true;
		setTimeout(() => {
			this.amountInput?.nativeElement.focus();
			this.amountInput?.nativeElement.select();
		});
	}

	commitEditing(): void {
		if (!this.isEditing) {
			return;
		}
		this.onManualAmountChange();
		this.isEditing = false;
	}

	setToMinimum(): void {
		this.setAmount(this.mininumAmount);
	}

	setToMaximum(): void {
		this.setAmount(this.maximumAmount);
	}

	selectQuickAmount(amount: number): void {
		this.setAmount(amount);
	}

	private setAmount(value: number): void {
		let newAmount = value;
		if (newAmount < this.mininumAmount) newAmount = this.mininumAmount;
		if (newAmount > this.maximumAmount) newAmount = this.maximumAmount;
		if (newAmount === this.amount) {
			return;
		}
		this.amount = newAmount;
		this.formatAmount();
		this.updateButtonsState();
		this.updateSliderProgress();
		this.emmitAmount();
	}

	private getFormattedAmount(): string {
		return this.currencyPipe.transform(this.amount, '$', 'symbol', '1.0-0');
	}

	ngOnInit(): void {
		this.formatAmount();
		this.updateButtonsState();
		this.updateSliderProgress();
		this.emmitAmount();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['amount'] && !changes['amount'].firstChange) {
			this.formatAmount();
			this.updateButtonsState();
			this.updateSliderProgress();
		}
	}

	onIncrementAmount(): void {
		console.log('onIncrement');
		if (this.nextIncrement() <= this.maximumAmount) {
			this.amount = this.nextIncrement();
			this.formatAmount();
			this.updateButtonsState();
			this.updateSliderProgress();
			this.emmitAmount();
		}
	}

	onDecrementAmount(): void {
		if (this.nextDecrement() >= this.mininumAmount) {
			this.amount = this.nextDecrement();
			this.formatAmount();
			this.updateButtonsState();
			this.updateSliderProgress();
			this.emmitAmount();
		}
	}

	onAmountRangeChange(): void {
		this.formatAmount();
		this.updateButtonsState();
		this.emmitAmount();
	}

	updateButtonsState(): void {
		this.isIncrementButtonDisable = this.nextIncrement() > this.maximumAmount;
		this.isDecrementButtonDisable = this.nextDecrement() < this.mininumAmount;
	}

	onManualAmountChange() {
		let amountWithoutFormat = this.formattedAmount.replace(/\D/g, '');
		// Redondeamos al múltiplo del salto configurado (`jump`) para que la
		// edición manual con el lápiz respete el mismo escalón que la barra.
		const step = parseFloat(this.jump) || 1;
		let newAmountSelected =
			Math.round(Number(amountWithoutFormat) / step) * step;
		if (newAmountSelected < this.mininumAmount)
			newAmountSelected = this.mininumAmount;
		if (newAmountSelected > this.maximumAmount)
			newAmountSelected = this.maximumAmount;
		if (newAmountSelected === this.amount) {
			this.formatAmount();
			return;
		}
		this.amount = newAmountSelected;
		this.formatAmount();
		this.updateButtonsState();
		this.updateSliderProgress();
		this.emmitAmount();
	}

	onChangeProgress() {
		this.formatAmount();
		this.updateSliderProgress();
	}

	onSliderInput(event: Event): void {
		const value = Number((event.target as HTMLInputElement).value);
		if (Number.isNaN(value)) {
			return;
		}
		this.amount = value;
		this.formatAmount();
		this.updateSliderProgress();
		this.amountLiveChange.emit(this.amount);
	}

	private nextDecrement() {
		return this.amount - parseFloat(this.jump);
	}

	private nextIncrement(): number {
		return this.amount + parseFloat(this.jump);
	}

	private formatAmount(): void {
		this.formattedAmount = this.getFormattedAmount();
	}

	private emmitAmount() {
		this.rangeAmountChange.emit(this.amount);
		this.amountLiveChange.emit(this.amount);
	}

	private updateSliderProgress() {
		this.sliderProgress =
			((this.amount - this.mininumAmount) * 100) /
			(this.maximumAmount - this.mininumAmount);
	}
}
